import React, { Component, Fragment } from "react";
import * as PropTypes from "prop-types";
import axios from "axios";
import { Button, Checkbox, Col, Form, Input, Spin, message, Modal, Row, Select, Switch } from "antd";

import { API_ERRO_TYPE_CANCEL } from "./../../config/general";

import { annualLeaveService, companiesService, departmentsService } from "../../redux/services";

import moment from "moment";

import {
	HourPickerWithMask,
	UIDrawerForm,
	UIFormSection,
	UIInfoBanner,
	UISteps,
	UISwitchField,
} from "./../../components";

import PeriodsTable from "./tables/periods"
import { debounce } from "lodash";
import { isCombinarFaixaDiurnaNoturnaEnabled } from "./../../helpers/auth";

const formId = `form-drawer-${Math.floor(Math.random() * 10001)}`;

import ModalRecalculate from './ModalRecalculate';

export const EXTRA_CONTROL_TYPES = {
	diario                : "Diário",
	semanal               : "Semanal",
	mensal                : "Mensal",
	primeira_faixa_entrada: "Somente a primeira faixa de extra",
	ultima_faixa_entrada  : "Somente a última faixa de extra",
	todas_horas_extras    : "Todas as faixas de horas extras",
};

export const ABSENCE_CONTROL_TYPES = {
	diario            : "Diário",
	semanal           : "Semanal",
	mensal            : "Mensal",
	todas_horas_faltas: "Todas as horas falta",
	nenhuma_hora_falta: "Nenhuma hora falta",
};

export const DELAY_CONTROL_TYPES = {
	nenhuma_hora_atraso: "Nenhuma hora atraso",
	todas_horas_atraso : "Todas as horas atraso",
};

export const EXTRA_DAYS = {
	uteis  : "Úteis",
	sabado : "Sábado",
	domingo: "Domingo",
	feriado: "Feriado",
	folga  : "Folga",
};

const IGNORED_EXTRA_FIELDS = Object.keys(EXTRA_DAYS).map((day) => `extra_${day}`);

export const STEPS = [
	{ key: "general", label: "Informações gerais", icon: <i className="fal fa-clipboard-list" /> },
	{ key: "periods", label: "Períodos", icon: <i className="fal fa-calendar-alt" /> },
	{ key: "extras", label: "Horas extras", icon: <i className="fal fa-business-time" /> },
	{ key: "absences", label: "Faltas e atrasos", icon: <i className="fal fa-user-times" /> },
	{ key: "ties", label: "Vínculos", icon: <i className="fal fa-building" /> },
];

export const STEP_FIELDS = {
	general : ["descricao", "is_active"],
	extras  : ["tipo_controle_extra", "limite_maximo_de_extras", "nao_multiplicar_pelo_percentual", "faixas_de_horario", "combinar_faixa_diurna_noturna", ...IGNORED_EXTRA_FIELDS],
	absences: ["controle_faltas", "limite_maximo_de_faltas", "controle_atraso"],
	ties    : ["empresas", "departamentos"],
};

const renderOptions = (options) => Object.entries(options).map(([value, label]) => (
	<Select.Option key={value} value={value}>{label}</Select.Option>
));

export const AnnualLeaveStatusField = () => (
	<Form.Item label="Status" required>
		<UISwitchField
			bordered
			title="Ativo"
			description="Bancos de horas inativos não são usados no cálculo do ponto.">
			<Form.Item name="is_active" valuePropName="checked" noStyle>
				<Switch />
			</Form.Item>
		</UISwitchField>
	</Form.Item>
);

export const AnnualLeaveInfoBanner = ({ description = "As horas extras, faltas e atrasos dos funcionários vinculados entram no banco conforme as regras das próximas etapas. Se o período começar antes de hoje, ao salvar você pode recalcular os cartões de ponto." }) => (
	<UIInfoBanner
		icon={<i className="fal fa-info-circle" />}
		title="Sobre o banco de horas"
		description={description}
	/>
);

export const AnnualLeaveExtrasFields = () => (
	<Fragment>
		<UIFormSection
			icon={<i className="fal fa-business-time" />}
			title="Controle de extras"
			description="Define quais horas extras entram no banco e o limite de cada período.">
			<Row gutter={16}>
				<Col xs={24} md={12}>
					<Form.Item name="tipo_controle_extra" label="Controle de extras">
						<Select
							optionFilterProp="children"
							filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
							allowClear
							placeholder="Selecione o controle de extras"
							showSearch>
							{renderOptions(EXTRA_CONTROL_TYPES)}
						</Select>
					</Form.Item>
				</Col>
				<Form.Item noStyle shouldUpdate={(prev, cur) => prev.tipo_controle_extra !== cur.tipo_controle_extra}>
					{({ getFieldValue }) => ["diario", "semanal", "mensal"].includes(getFieldValue("tipo_controle_extra")) && (
						<Col xs={24} md={12}>
							<HourPickerWithMask name="limite_maximo_de_extras" label="Limite máximo de extras" placeholder="HH:mm" required />
						</Col>
					)}
				</Form.Item>
			</Row>
			<Row gutter={16}>
				<Col xs={24} md={12}>
					<Form.Item label="Percentual">
						<UISwitchField
							bordered
							title="Multiplicar pelo percentual"
							description="Nas faixas selecionadas, as horas extras entram no banco acrescidas do percentual da faixa.">
							<Form.Item name="nao_multiplicar_pelo_percentual" valuePropName="checked" noStyle>
								<Switch />
							</Form.Item>
						</UISwitchField>
					</Form.Item>
					<Form.Item noStyle shouldUpdate={(prev, cur) => prev.nao_multiplicar_pelo_percentual !== cur.nao_multiplicar_pelo_percentual}>
						{({ getFieldValue }) => getFieldValue("nao_multiplicar_pelo_percentual") && (
							<Form.Item name="faixas_de_horario" label="Faixas de horário">
								<Select
									mode="multiple"
									optionFilterProp="children"
									filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
									allowClear
									placeholder="Selecione a(s) faixa(s)"
									showSearch>
									{renderOptions(EXTRA_DAYS)}
								</Select>
							</Form.Item>
						)}
					</Form.Item>
				</Col>
				<Form.Item noStyle shouldUpdate={(prev, cur) => prev.tipo_controle_extra !== cur.tipo_controle_extra}>
					{({ getFieldValue }) => ["primeira_faixa_entrada", "ultima_faixa_entrada", "diario", "semanal", "mensal"].includes(getFieldValue("tipo_controle_extra")) && isCombinarFaixaDiurnaNoturnaEnabled() && (
						<Col xs={24} md={12}>
							<Form.Item label="Faixas diurna e noturna">
								<UISwitchField
									bordered
									title="Não separar faixa diurna e noturna"
									description="As extras diurnas e noturnas são tratadas como uma faixa só.">
									<Form.Item name="combinar_faixa_diurna_noturna" valuePropName="checked" noStyle>
										<Switch />
									</Form.Item>
								</UISwitchField>
							</Form.Item>
						</Col>
					)}
				</Form.Item>
			</Row>
		</UIFormSection>
		<Form.Item noStyle shouldUpdate={(prev, cur) => IGNORED_EXTRA_FIELDS.some((name) => prev[name] !== cur[name])}>
			{({ getFieldValue, setFieldsValue }) => {
				const checkedCount = IGNORED_EXTRA_FIELDS.filter((name) => getFieldValue(name)).length;

				return (
					<UIFormSection
						icon={<i className="fal fa-calendar-times" />}
						title="Ignorar faixa de extras"
						description="As horas extras feitas nos dias marcados não entram no banco."
						extra={
							<Checkbox
								checked={checkedCount === IGNORED_EXTRA_FIELDS.length}
								indeterminate={checkedCount > 0 && checkedCount < IGNORED_EXTRA_FIELDS.length}
								onChange={(e) => setFieldsValue(Object.fromEntries(IGNORED_EXTRA_FIELDS.map((name) => [name, e.target.checked])))}>
								Selecionar todos
							</Checkbox>
						}>
						<Row gutter={16}>
							{Object.entries(EXTRA_DAYS).map(([day, label]) => (
								<Col key={day} xs={12} md={8}>
									<Form.Item name={`extra_${day}`} valuePropName="checked">
										<Checkbox>{label}</Checkbox>
									</Form.Item>
								</Col>
							))}
						</Row>
					</UIFormSection>
				);
			}}
		</Form.Item>
	</Fragment>
);

export const AnnualLeaveAbsencesFields = () => (
	<Fragment>
		<UIFormSection
			icon={<i className="fal fa-user-times" />}
			title="Faltas"
			description="Define quais faltas são descontadas do banco. O que passar do limite continua como falta no cartão de ponto.">
			<Row gutter={16}>
				<Col xs={24} md={12}>
					<Form.Item name="controle_faltas" label="Controle de faltas">
						<Select
							optionFilterProp="children"
							filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
							allowClear
							placeholder="Selecione o controle de faltas"
							showSearch>
							{renderOptions(ABSENCE_CONTROL_TYPES)}
						</Select>
					</Form.Item>
				</Col>
				<Form.Item noStyle shouldUpdate={(prev, cur) => prev.controle_faltas !== cur.controle_faltas}>
					{({ getFieldValue }) => !["nenhuma_hora_falta", "todas_horas_faltas"].includes(getFieldValue("controle_faltas")) && (
						<Col xs={24} md={12}>
							<HourPickerWithMask name="limite_maximo_de_faltas" label="Limite máximo de faltas" placeholder="HH:mm" required />
						</Col>
					)}
				</Form.Item>
			</Row>
		</UIFormSection>
		<UIFormSection
			icon={<i className="fal fa-user-clock" />}
			title="Atrasos"
			description="Define se os atrasos também são descontados do banco.">
			<Row gutter={16}>
				<Col xs={24} md={12}>
					<Form.Item name="controle_atraso" label="Controle de atrasos">
						<Select
							optionFilterProp="children"
							filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
							allowClear
							placeholder="Selecione o controle de atrasos"
							showSearch>
							{renderOptions(DELAY_CONTROL_TYPES)}
						</Select>
					</Form.Item>
				</Col>
			</Row>
		</UIFormSection>
	</Fragment>
);

class Create extends Component {
	static propTypes = {
		visible: PropTypes.bool.isRequired,
		onComplete: PropTypes.func.isRequired,
		onClose: PropTypes.func.isRequired,
	};

	constructor(props) {
		super(props);

		this.state = {
			isLoading: false,
			isSending: false,
			uuid: 0,
			initialDate: '',
			create: true,
			vinculos_outros_bancos: {},
			activeKey: "general",

			companies: [],
			companiesIsLoading: false,
			selectedCompanies: [],
			departments: [],
			departmentsIsLoading: false,
			disabledSelectAllDepartments: false,
			blockedAllDepartments: false,
			recalcularModalVisible: false,
			selectedDepartments: [],
		};

		this._axiosCancelCompaniesToken = null;
		this._axiosCancelDepartmentsToken = null;
	}

	onOpen = () => {
		this.setState({
			activeKey: "general",
			vinculos_outros_bancos: {},
			selectedCompanies: [],
			departments: [],
			disabledSelectAllDepartments: false,
			blockedAllDepartments: false,
		});

		setTimeout(() => {
			this.descricao && this.descricao.focus()
			this.form.setFieldsValue({
				is_active: 1,
			});
		}, 100);
	};

	validVigencyToLink = (othersVigencies) => {
		const currentVigencies = this.periodsTable?.state?.periods?.map((period) => ({ vigencia_inicio: moment(period.data_inicial).format('YYYY-MM-DD'), vigencia_final: moment(period.data_final).format('YYYY-MM-DD') }));

		if (currentVigencies.every(item => item.vigencia_inicio === 'Data inválida')) return true;
		const result = currentVigencies.some(
			current => othersVigencies.some(other => {
				const currentStart = moment(current.vigencia_inicio).startOf('day');
				const currentEnd = moment(current.vigencia_final).endOf('day');
				const otherStart = moment(other.vigencia_inicio).startOf('day');
				const otherEnd = moment(other.vigencia_final).endOf('day');

				return currentStart.isBefore(otherEnd) && otherStart.isBefore(currentEnd);
			})
		);

		return result;
	}

	filterAllowedCompaniesDepartaments = () => {
		const { departments, vinculos_outros_bancos, selectedCompanies } = this.state;
		const { vinculoEmpresaDepartamento, vinculoEmpresaTodosDep, vinculoGeral } = vinculos_outros_bancos;

		let filteredDepartments = departments;
		const paramsToUpdate = {};

		const isSelectedAllCompanies = selectedCompanies.includes('todos') || !selectedCompanies.length;
		const selectedCompanyHasAllDepartments = vinculoEmpresaTodosDep?.some(item => {
			if (isSelectedAllCompanies || selectedCompanies.includes(item.empresa_id)) {
				return this.validVigencyToLink(item.vigencias);
			}

			return false;
		});
		const vinculoGeralValidationConflict = vinculoGeral?.some(
			item => this.validVigencyToLink(item.banco_horas_vigencia)
		);

		if (
			vinculoGeralValidationConflict ||
			selectedCompanyHasAllDepartments
		) {
			this.setState({
				departments: [],
				disabledSelectAllDepartments: true,
				blockedAllDepartments: true,
			}, () => {
				this.form.setFieldValue("departamentos", []);
			});

			return;
		} else {
			paramsToUpdate.blockedAllDepartments = false;
			paramsToUpdate.disabledSelectAllDepartments = false;
		}

		vinculoEmpresaDepartamento?.forEach((item) => {
			if (
				(
					item.empresa_id === null ||
					selectedCompanies.includes(item.empresa_id) ||
					isSelectedAllCompanies
				) &&
				this.validVigencyToLink(item.vigencias)
			) {
				filteredDepartments = filteredDepartments.filter(
					(department) => !item.departamentos_ids.includes(department.id)
				);

				paramsToUpdate.disabledSelectAllDepartments = true;

				this.form.setFieldValue("departamentos", []);
			} else {
				paramsToUpdate.disabledSelectAllDepartments = false;
			}
		});

		this.setState({
			departments: filteredDepartments,
			...paramsToUpdate,
		});
	}


	fetchCompanies = () => {
		if (this._axiosCancelCompaniesToken) {
			this._axiosCancelCompaniesToken.cancel("Only one request allowed at a time.");
		}

		this._axiosCancelCompaniesToken = axios.CancelToken.source();

		this.setState({
			companiesIsLoading: true,
		});

		companiesService.getAutocomplete({
			orderBy: "nome:asc",
			cancelToken: this._axiosCancelCompaniesToken.token,
		})
			.then((response) => {
				this.setState({
					companiesIsLoading: false,
					companies: response.data.data,
				});
			})
			.catch((data) => {
				if (data.error_type === API_ERRO_TYPE_CANCEL) return null;

				this.setState({
					companiesIsLoading: false,
				});

				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
				});
			});
	};

	fetchDepartments = (value) => {
		if (this._axiosCancelDepartmentsToken) {
			this._axiosCancelDepartmentsToken.cancel("Only one request allowed at a time.");
		}

		this._axiosCancelDepartmentsToken = axios.CancelToken.source();

		// if (value !== undefined && !value.trim().length) {
			// this.setState({
				// departmentsIsLoading: false,
				// departments: [],
			// });

			// return false;
		// }

		this.setState({
			departmentsIsLoading: true,
		});

		let params = {
			...(value ? { search: value } : {}),
			orderBy: "nome:asc",
			cancelToken: this._axiosCancelDepartmentsToken.token,
			empresas_ids: this.form.getFieldValue("empresas").includes("todos") ? [] : this.form.getFieldValue("empresas"),
		}

		departmentsService.getAutocomplete({
			...params
		})
			.then((response) => {
				this.setState({
					departmentsIsLoading: false,
					departments: response.data.data,
				}, () => {
					this.filterAllowedCompaniesDepartaments();
				});
			})
			.catch((data) => {
				if (data.error_type === API_ERRO_TYPE_CANCEL) return null;

				this.setState({
					departmentsIsLoading: false,
				});

				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
				});
			});
	};

	fetchDepartmentsSearch = debounce(this.fetchDepartments, 500);

	fetchVinculosOutrosBancos = () => {
		annualLeaveService.getVinculosOutrosBanco()
			.then((response) => {
				const { data } = response;
				this.setState({
					vinculos_outros_bancos: data,
				}, () => {
					this.filterAllowedCompaniesDepartaments();
				});
			})
			.catch();
	}

	onCompanyChange = (value) => {
		let selectedCompanies = value;
		if (value.length > 1 && value.includes("todos")) {
			selectedCompanies = value.filter((item) => item !== "todos")
			this.form.setFieldValue("empresas", value.filter((item) => item !== "todos"));
		}

		this.setState({
			employees: [],
			employeesIsLoading: false,
			departments: [],
			departmentsIsLoading: false,
			selectedCompanies,
		}, () => {
			this.filterAllowedCompaniesDepartaments();
		});

		this.form.setFieldValue("departamentos", ["todos"]);
	};

	onCompanySelect = (value) => {
		if (value === "todos") {
			this.form.setFieldValue("empresas", ["todos"]);

			this.setState({
				selectedCompanies: ["todos"],
			},  () => {
				this.filterAllowedCompaniesDepartaments();
			});
		}
	};

	onDepartmentChange = (value) => {
		if (value.length > 1 && value.includes("todos")) {
			this.form.setFieldValue("departamentos", value.filter((item) => item !== "todos"));
		}
	};

	onDepartmentSelect = (value) => {
		if (value === "todos") {
			this.form.setFieldValue("departamentos", ["todos"]);
		}
	};

	resetFields = () => {
	};

	onClose = () => {
		// Reset fields
		this.resetFields();

		// Callback
		this.props.onClose();
	};

	onFinish = (values) => {
		this.setState({
			isSending: true,
		});

		const data = { ...values };

		if (!(data.empresas instanceof Array)) {
			data.empresas = [data.empresas];
		}

		if (!(data.departamentos instanceof Array)) {
			data.departamentos = [data.departamentos];
		}

		if (data.empresas.includes("todos")) {
			data.empresas = null;
			data.todas_empresas = true;
		}

		if (data.departamentos.includes("todos")) {
			data.todas_departamentos = true;
			data.departamentos = null;
		}

		if (data.faixas_de_horario) {

			data.faixas_de_horario.forEach((faixa) => {
				data[`multiplicar_percentual_${faixa}`] = true;
			})

		}

		data.vigencia = this.periodsTable?.state?.periods?.map((period) => ({ vigencia_inicio: moment(period.data_inicial).format('YYYY-MM-DD'), vigencia_final: moment(period.data_final).format('YYYY-MM-DD') }));

		Object.keys(data).forEach(key => {
			if (key.includes('_data_incial')) {
				this.setState({
					initialDate: moment(data[key]).format('YYYY-MM-DD'),
				})
			}
		});

		annualLeaveService.create(data)
			.then((response) => {
				this.setState({
					isSending: false,
					uuid: response.data.data.uuid,
				});

				// Reset fields
				this.resetFields();

				// Success message
				message.success("Banco de horas cadastrado com sucesso.");

				const currentDate = moment().format();

				const isInitialDateBeforeCurrentDate = moment(this.state.initialDate).isBefore(currentDate);

				if (isInitialDateBeforeCurrentDate) {
					this.recalculateOpen(this.state.uuid);
				}

				this.props.onComplete();

			})
			.catch((data) => {
				this.setState({
					isSending: false,
				});

				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
				});
			});
	};

	onFinishFailed = ({ errorFields }) => {
		const field = errorFields[0]?.name[0];
		const step = STEPS.find(({ key }) => this.getStepFields(key).includes(field));

		if (step && step.key !== this.state.activeKey) {
			this.setActiveKey(step.key, () => this.form.scrollToField(field));
		}
	};

	getStepFields = (key) => {
		if (key === "periods") {
			return (this.periodsTable?.state?.periods ?? []).flatMap(({ id }) => [`${id}_data_incial`, `${id}_data_final`]);
		}

		return STEP_FIELDS[key] ?? [];
	};

	setActiveKey = (activeKey, callback) => {
		this.setState({ activeKey }, callback);

		if (activeKey === "ties") {
			this.fetchVinculosOutrosBancos();
		}
	};

	goToNextStep = () => {
		const { activeKey } = this.state;
		const nextStep = STEPS[STEPS.findIndex((step) => step.key === activeKey) + 1];

		this.form.validateFields(this.getStepFields(activeKey))
			.then(() => this.setActiveKey(nextStep.key))
			.catch(({ errorFields }) => errorFields.length && this.form.scrollToField(errorFields[0].name));
	};

	recalculateOpen = (uuid) => {
		this.setState({
			recalcularModalVisible: true,
			isLoading: false,
			isSending: false,
		});


		// On open screen
		this.recalculateScreen.onOpen(uuid);
	};

	recalculateOnClose = () => {
		this.setState({ recalcularModalVisible: false });

		// Success message
		message.success("Registro atualizado com sucesso.");

		// Callback
		this.props.onComplete();
	};

	recalculateError = () => {
		this.setState({ recalcularModalVisible: false });

		message.error("Ocorreu um erro inesperado! Tenta novamente.");
		// Callback
		this.props.onComplete();
	};

	recalculateOnComplete = () => {
		this.setState({ recalcularModalVisible: false }, () => {
			this.setState({
				isSending: false,
			});

			// Success message
			message.success("Cálculo realizado com sucesso.");

			// Callback
			this.props.onComplete();
		});
	};

	renderFooter = () => {
		const { isSending, activeKey } = this.state;

		const currentIndex = STEPS.findIndex((step) => step.key === activeKey);
		const prevStep = STEPS[currentIndex - 1];
		const isLastStep = currentIndex === STEPS.length - 1;

		return (
			<div className="drawer-form-wizard-footer">
				<div className="drawer-form-wizard-footer-actions">
					<Button onClick={this.onClose} disabled={isSending}>Cancelar</Button>
					{prevStep && (
						<Button onClick={() => this.setActiveKey(prevStep.key)} disabled={isSending} icon={<i className="fal fa-arrow-left" />}>
							Voltar etapa
						</Button>
					)}
				</div>
				<div className="drawer-form-wizard-footer-actions">
					{isLastStep ? (
						<Button key="submit" type="primary" form={formId} htmlType="submit" loading={isSending} icon={<i className="fal fa-check" />}>
							Concluir
						</Button>
					) : (
						<Button key="next" type="primary" onClick={this.goToNextStep} disabled={isSending} icon={<i className="fal fa-arrow-right" />}>
							Próximo
						</Button>
					)}
				</div>
			</div>
		);
	};

	render() {
		const { visible } = this.props;

		const {
			isLoading,
			isSending,
			companies,
			companiesIsLoading,
			departments,
			departmentsIsLoading,
			recalcularModalVisible,
			disabledSelectAllDepartments,
			blockedAllDepartments,
			activeKey,
		} = this.state;

		return (
			<Fragment>
				<UIDrawerForm
					visible={visible}
					width={1100}
					onClose={this.onClose}
					isLoading={isLoading}
					isSending={isSending}
					formId={formId}
					showBtnSave={false}
					icon={<i className="fal fa-hourglass-half" />}
					title="Incluir novo banco de horas"
					subtitle="Defina as regras do banco de horas e a quem ele se aplica."
					steps={
						<UISteps
							steps={STEPS}
							current={activeKey}
							onChange={(key) => this.setActiveKey(key)}
							hideNav
						/>
					}
					footer={this.renderFooter()}>
					<Form
						ref={el => this.form = el}
						id={formId}
						layout="vertical"
						scrollToFirstError
						onFinish={this.onFinish}
						onFinishFailed={this.onFinishFailed}
						initialValues={{
							is_active: true,
							nao_multiplicar_pelo_percentual: false,
							combinar_faixa_diurna_noturna: false,
							tipo_controle_extra: 'primeira_faixa_entrada',
							controle_faltas: 'diario',
							controle_atraso: 'nenhuma_hora_atraso',
							empresas: ["todos"],
							departamentos: ["todos"],
						}}>
						<div style={{ display: activeKey === "general" ? "block" : "none" }}>
							<UIFormSection
								icon={<i className="fal fa-clipboard-list" />}
								title="Informações gerais"
								description="Identificação e situação do banco de horas.">
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item name="descricao" label="Descrição" hasFeedback rules={[{ required: true, message: "Campo obrigatório." }]}>
											<Input ref={el => this.descricao = el} placeholder="Digite a descrição do banco de horas" />
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<AnnualLeaveStatusField />
									</Col>
								</Row>
							</UIFormSection>
							<AnnualLeaveInfoBanner />
						</div>
						<div style={{ display: activeKey === "periods" ? "block" : "none" }}>
							<UIFormSection
								icon={<i className="fal fa-calendar-alt" />}
								title="Períodos"
								description="Datas de início e fim em que o banco de horas fica valendo.">
								<PeriodsTable ref={el => this.periodsTable = el} />
							</UIFormSection>
						</div>
						<div style={{ display: activeKey === "extras" ? "block" : "none" }}>
							<AnnualLeaveExtrasFields />
						</div>
						<div style={{ display: activeKey === "absences" ? "block" : "none" }}>
							<AnnualLeaveAbsencesFields />
						</div>
						<div style={{ display: activeKey === "ties" ? "block" : "none" }}>
							<UIFormSection
								icon={<i className="fal fa-building" />}
								title="Empresas e departamentos"
								description="Define a quais empresas e departamentos o banco de horas se aplica.">
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item name="empresas" label="Empresa" rules={[{ required: true, message: "Campo obrigatório." }]}>
											<Select
												ref={el => this.empresas = el}
												mode="multiple"
												optionFilterProp="children"
												filterOption={(input, option) => (typeof option.children === 'string' ? option.children : option.children.props.children).toLowerCase().indexOf(input.toLowerCase()) >= 0}
												allowClear
												placeholder="Selecione a(s) empresa(s)"
												notFoundContent={companiesIsLoading ? <Spin indicator={<i className="fad fa-spinner-third fa-spin" />} /> : null}
												onDropdownVisibleChange={(open) => open && this.fetchCompanies()}
												onSearch={this.fetchCompanies}
												onChange={this.onCompanyChange}
												onSelect={this.onCompanySelect}
												showSearch>
												<Select.Option value="todos">Todas</Select.Option>
												{companies.map((item, index) => (
													<Select.Option key={index} value={item.id}>
														{item.nome}
													</Select.Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item
											name="departamentos"
											label="Departamento"
											rules={[{ required: true, message: "Campo obrigatório." }]}
											extra={blockedAllDepartments ? "Uma ou mais empresas selecionadas já possuem um banco de horas vinculado a todos os departamentos. Selecione outra empresa ou ajuste os vínculos existentes para continuar." : null}>
											<Select
												mode="multiple"
												optionFilterProp="children"
												filterOption={(input, option) => (typeof option.children === 'string' ? option.children : option.children.props.children).toLowerCase().indexOf(input.toLowerCase()) >= 0}
												allowClear
												placeholder="Selecione o(s) departamento(s)"
												notFoundContent={departmentsIsLoading ? <Spin indicator={<i className="fad fa-spinner-third fa-spin" />} /> : null}
												onDropdownVisibleChange={(open) => open && this.fetchDepartments()}
												onSearch={this.fetchDepartmentsSearch}
												onChange={this.onDepartmentChange}
												onSelect={this.onDepartmentSelect}
												disabled={blockedAllDepartments}
												showSearch>
												{!disabledSelectAllDepartments && <Select.Option value="todos">Todos</Select.Option>}
												{departments.map((item, index) => (
													<Select.Option key={index} value={item.id}>{item.nome}</Select.Option>
												))}
											</Select>
										</Form.Item>
									</Col>
								</Row>
							</UIFormSection>
						</div>
					</Form>
				</UIDrawerForm>
				<ModalRecalculate
					ref={el => this.recalculateScreen = el}
					visible={recalcularModalVisible}
					onComplete={this.recalculateOnComplete}
					onClose={this.recalculateOnClose}
					create={this.state.create}
					recalculateError={this.recalculateError}
				/>
			</Fragment>
		)
	}
}

export default Create;
